import { useEffect, useMemo, useState } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageLoader } from "../components/ui/page-loader";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { Activity, Users, Clock, Layers, Calendar as CalendarIcon, Eye, RefreshCw, Trash2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer } from "recharts";

const isoToday = () => new Date().toISOString().slice(0, 10);

const fmtDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return iso; }
};

const methodBadge = (m) => {
  const map = {
    GET: "bg-slate-100 text-slate-700",
    POST: "bg-emerald-100 text-emerald-700",
    PUT: "bg-blue-100 text-blue-700",
    PATCH: "bg-amber-100 text-amber-700",
    DELETE: "bg-red-100 text-red-700",
  };
  return <Badge variant="outline" className={`font-mono text-xs ${map[m] || "bg-gray-100"}`}>{m}</Badge>;
};

export default function AttivitaUtentiPage() {
  const [loading, setLoading] = useState(true);
  const [dataDa, setDataDa] = useState(isoToday());
  const [dataA, setDataA] = useState(isoToday());
  const [dashboard, setDashboard] = useState(null);
  const [retention, setRetention] = useState(null);
  const [purging, setPurging] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [detailHourly, setDetailHourly] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const [dashRes, retRes] = await Promise.all([
        api.get(`${prefix}/activity-log/dashboard?data_da=${dataDa}&data_a=${dataA}`),
        api.get(`${prefix}/activity-log/retention`).catch(() => ({ data: null })),
      ]);
      setDashboard(dashRes.data);
      setRetention(retRes.data);
    } catch (err) {
      handleApiError(err, "Impossibile caricare l'attività utenti");
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePurge = async () => {
    if (!retention || retention.rows_eligible_for_purge === 0) return;
    const ok = window.confirm(
      `Eliminare ${retention.rows_eligible_for_purge} righe più vecchie di ${retention.retention_days} giorni?`
    );
    if (!ok) return;
    setPurging(true);
    try {
      const prefix = getApiPrefix();
      const res = await api.post(`${prefix}/activity-log/purge`);
      showSuccess(`${res.data.rows_deleted} righe eliminate in ${res.data.duration_seconds}s`);
      await load();
    } catch (err) {
      handleApiError(err, "Purge fallito");
    } finally {
      setPurging(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totali = dashboard?.totali || { azioni: 0, utenti_attivi: 0, ore_totali: 0 };
  const utenti = dashboard?.utenti || [];
  const perMetodo = dashboard?.per_metodo || [];
  const perRisorsa = dashboard?.per_risorsa || [];

  const chartUtenti = useMemo(() => utenti.map(u => ({
    name: (u.user_label || u.user_id).split("@")[0].slice(0, 18),
    ore: u.ore_totali,
    azioni: u.azioni_totali,
  })), [utenti]);

  const openUserDetail = async (u) => {
    setDetailUser(u);
    setDetailLoading(true);
    try {
      const prefix = getApiPrefix();
      const [timelineRes, hourlyRes] = await Promise.all([
        api.get(`${prefix}/activity-log/timeline?data=${dataDa}&user_id=${u.user_id}&page_size=500`),
        api.get(`${prefix}/activity-log/user-hourly?data=${dataDa}&user_id=${u.user_id}`),
      ]);
      setDetailItems(timelineRes.data.items || []);
      setDetailHourly((hourlyRes.data.ore || []).map(h => ({ ora: `${h.ora}:00`, count: h.count })));
    } catch (err) {
      handleApiError(err, "Impossibile caricare il dettaglio");
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && !dashboard) return <PageLoader variant="section" text="Caricamento attività..." />;

  return (
    <div className="space-y-6" data-testid="attivita-utenti-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-indigo-600" />
            Attività Utenti
          </h1>
          <p className="text-sm text-muted-foreground">Registro attività per utente e ore di lavoro</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground">Dal</label>
            <Input type="date" value={dataDa} onChange={(e) => setDataDa(e.target.value)} className="h-9" data-testid="activity-data-da" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Al</label>
            <Input type="date" value={dataA} onChange={(e) => setDataA(e.target.value)} className="h-9" data-testid="activity-data-a" />
          </div>
          <Button onClick={load} className="h-9" data-testid="activity-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Aggiorna
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-indigo-500" data-testid="kpi-utenti">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Utenti attivi</div>
                <div className="text-3xl font-bold">{totali.utenti_attivi}</div>
              </div>
              <Users className="h-8 w-8 text-indigo-500 opacity-70" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500" data-testid="kpi-azioni">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Azioni totali</div>
                <div className="text-3xl font-bold">{totali.azioni.toLocaleString("it-IT")}</div>
              </div>
              <Layers className="h-8 w-8 text-emerald-500 opacity-70" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500" data-testid="kpi-ore">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Ore totali (stimate)</div>
                <div className="text-3xl font-bold">{totali.ore_totali.toFixed(2)}h</div>
              </div>
              <Clock className="h-8 w-8 text-amber-500 opacity-70" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Retention panel */}
      {retention && (
        <Card className="bg-slate-50 border-slate-200" data-testid="retention-panel">
          <CardContent className="pt-4 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="text-sm space-y-0.5">
              <div className="font-medium">Retention log attività</div>
              <div className="text-muted-foreground text-xs">
                Conservazione: <span className="font-mono font-semibold">{retention.retention_days} giorni</span> ·
                Righe totali: <span className="font-mono font-semibold">{retention.total_rows.toLocaleString("it-IT")}</span> ·
                Eliminabili subito: <span className="font-mono font-semibold text-amber-700">{retention.rows_eligible_for_purge.toLocaleString("it-IT")}</span>
              </div>
              <div className="text-muted-foreground text-xs">
                Job automatico: ogni giorno alle 03:15 (UTC).
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePurge}
              disabled={purging || retention.rows_eligible_for_purge === 0}
              data-testid="btn-purge-now"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {purging ? "Pulizia..." : `Pulisci ora (${retention.rows_eligible_for_purge})`}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Per-user chart */}
      {chartUtenti.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ore lavorate per utente</CardTitle>
            <CardDescription>Delta tra prima e ultima attività di ogni giornata</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartUtenti}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={11} />
                <YAxis fontSize={11} />
                <ReTooltip />
                <Bar dataKey="ore" fill="#6366f1" name="Ore" />
                <Bar dataKey="azioni" fill="#10b981" name="Azioni" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Utenti table */}
        <Card className="lg:col-span-2" data-testid="utenti-table-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Riepilogo per utente</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utente</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                  <TableHead className="text-right">Giorni</TableHead>
                  <TableHead className="text-right">Ore stimate</TableHead>
                  <TableHead>Prima</TableHead>
                  <TableHead>Ultima</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {utenti.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Nessuna attività registrata nel periodo selezionato.
                    </TableCell>
                  </TableRow>
                ) : utenti.map((u) => (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <TableCell className="font-medium">{u.user_label}</TableCell>
                    <TableCell className="text-right font-mono">{u.azioni_totali}</TableCell>
                    <TableCell className="text-right">{u.giorni_attivi}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{u.ore_totali.toFixed(2)}h</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(u.prima_attivita)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(u.ultima_attivita)}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => openUserDetail(u)} data-testid={`view-user-${u.user_id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Right column stats */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Per metodo</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {perMetodo.length === 0 ? <p className="text-xs text-muted-foreground">-</p> : perMetodo.map(m => (
                <div key={m.method} className="flex items-center justify-between text-sm">
                  <span>{methodBadge(m.method)}</span>
                  <span className="font-mono">{m.count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Aree più usate</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {perRisorsa.length === 0 ? <p className="text-xs text-muted-foreground">-</p> : perRisorsa.slice(0, 10).map(r => (
                <div key={r.resource} className="flex items-center justify-between text-sm">
                  <span className="truncate mr-2 text-slate-600">{r.resource}</span>
                  <span className="font-mono">{r.count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* User detail dialog */}
      <Dialog open={!!detailUser} onOpenChange={(open) => { if (!open) { setDetailUser(null); setDetailItems([]); setDetailHourly([]); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" /> Attività di {detailUser?.user_label} — {dataDa}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="text-center py-12 text-muted-foreground">Caricamento...</div>
          ) : (
            <div className="space-y-4">
              {detailHourly.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Distribuzione oraria</h4>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={detailHourly}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="ora" fontSize={10} />
                      <YAxis fontSize={10} allowDecimals={false} />
                      <ReTooltip />
                      <Bar dataKey="count" fill="#6366f1" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div>
                <h4 className="text-sm font-medium mb-1">Cronologia ({detailItems.length} azioni)</h4>
                <div className="max-h-96 overflow-y-auto border rounded">
                  <Table>
                    <TableHeader className="sticky top-0 bg-slate-50">
                      <TableRow>
                        <TableHead className="w-24">Ora</TableHead>
                        <TableHead className="w-16">Metodo</TableHead>
                        <TableHead>Percorso</TableHead>
                        <TableHead className="w-16">Stato</TableHead>
                        <TableHead className="w-20 text-right">ms</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailItems.map(i => (
                        <TableRow key={i.id}>
                          <TableCell className="text-xs font-mono text-muted-foreground">{fmtDate(i.created_at)}</TableCell>
                          <TableCell>{methodBadge(i.method)}</TableCell>
                          <TableCell className="text-xs font-mono truncate max-w-md" title={i.path}>{i.path}</TableCell>
                          <TableCell><span className={`text-xs font-mono ${i.status_code >= 400 ? "text-red-600" : "text-emerald-600"}`}>{i.status_code}</span></TableCell>
                          <TableCell className="text-right text-xs font-mono">{i.duration_ms}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
